import React, { useState } from 'react';
import AddBucketIcon from '@svg/add_bucket_icon';

import { Modal, Button, LoadingButton } from '@common/index';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField } from '@mui/material';

import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createBucket } from '@requests/buckets';

export const CreateBucket: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();
  const { service_id } = useParams();

  const username = useSelector((state: State) => state.auth.username);
  const open = useSelector((state: State) => state.flow.createBucket);
  const setOpen = (val: boolean) => dispatch(flowActions.setCreateBucket(val));

  const [name, setName] = useState('');
  const [region, setRegion] = useState('us-east-1');

  const handleRegionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegion((event.target as HTMLInputElement).value);
  };

  const convertName = (input: string) => {
    let formatted = input.trim().replace(/ /g, '-');
    formatted = formatted.replace(/_/g, '-');
    formatted = formatted.replace(/[^a-zA-Z0-9\-.]/g, '');
    return formatted.toLowerCase();
  };

  const { mutateAsync: save, status: saveStatus } = useMutation({
    mutationFn: createBucket,
    onSuccess: (data) => {
      if (!data) return;

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to create bucket!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });

      enqueueSnackbar('Bucket created successfully!', { variant: 'success', autoHideDuration: 3000 });
      navigate(`/${service_id}/buckets/${name}`);
      setOpen(false);
      setName('');
    },
  });

  const isBucketSaving = saveStatus === 'pending';

  const create = () => {
    enqueueSnackbar('Creating bucket...', { variant: 'info', autoHideDuration: 3000 });

    save({
      name: name as string,
      user_id: username as string,
      service_id: service_id as string,
      region: region as string,
    });
  };

  const isInputError = (name.length < 4 || name.length > 65) && name.length !== 0;

  return (
    <Modal
      open={Boolean(open)}
      setOpen={setOpen}
      background='primary'
      header='Add a Bucket'
      info={
        'A bucket is a collection of files. The files in your buckets will be converted to vector embeddings which enables a large language model to read them before responding to queries.'
      }
      icon={<AddBucketIcon height={19} color='#000000' />}
      body={
        <>
          <TextField
            variant='standard'
            fullWidth
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            required
            label='Bucket Name'
            value={name}
            placeholder={'E.g. documents'}
            error={isInputError}
            helperText={
              isInputError
                ? `Must be ${name.length < 4 ? 'at least 3 characters' : 'less than 65 characters'}`
                : undefined
            }
            onChange={(e) => setName(convertName(e.target.value))}
            sx={{ mb: 4 }}
          />
          <FormControl>
            <FormLabel>Region</FormLabel>
            <RadioGroup row value={region} onChange={handleRegionChange}>
              <FormControlLabel value='us-east-1' control={<Radio />} label='US East' />
              <FormControlLabel value='us-central-1' control={<Radio />} label='US Central' />
              <FormControlLabel value='us-west-1' control={<Radio />} label='US West' />
            </RadioGroup>
          </FormControl>
        </>
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button
            small
            disabled={isBucketSaving}
            onClick={() => {
              setOpen(false);
            }}
          >
            Back
          </Button>
          <LoadingButton
            small
            customColor='primary'
            onClick={create}
            minWidth='70px'
            loading={isBucketSaving}
            disabled={isInputError || !name}
          >
            Create
          </LoadingButton>
        </Stack>
      }
    />
  );
};
